import React, { useCallback, useMemo, useState } from "react";
import cn from "clsx";
import { useTranslation } from "react-i18next";
import map from "lodash/map";
import find from "lodash/find";

import CustomDropdown from "@/components/customDropdown";
import View from "../../../components/common/view";
import { setSearchText } from "../../../redux/actions/common.action";
import { useAppDispatch } from "../../../redux/store";
import { getCategoryData, getSortedCategories } from "../shopByBrands";
import { BrandData, SelectedCategory } from "../types";
import "./style.scss";

const CategoriesTabs: React.FC<{
  tabs: SelectedCategory[];
  selectedTab: SelectedCategory;
  selectedBrandChar: string;
  categoryData: string[];
  brands: BrandData[];
  setSelectedCategory: React.Dispatch<React.SetStateAction<SelectedCategory>>;
  setSelectedBrandChar: React.Dispatch<React.SetStateAction<string>>;
  searchText: string;
}> = ({
  tabs,
  selectedTab,
  categoryData,
  brands,
  setSelectedCategory,
  setSelectedBrandChar,
  selectedBrandChar,
  searchText
}) => {
  const { t } = useTranslation("shopByBrands");
  const dispatch = useAppDispatch();
  const [yScrollPosition, setYScrollPosition] = useState(0);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearchText(e.target.value));
  };

  const changeCategory = useCallback(
    (category: SelectedCategory) => {
      const data = getCategoryData(brands, category);
      const sortedCategories = getSortedCategories(data);

      setSelectedCategory(category);
      setSelectedBrandChar(sortedCategories[0]);
    },
    [brands]
  );

  const scrollHandler = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    brandChar: string
  ) => {
    if (event) {
      event.preventDefault();
    }

    const previousYScrollPosition = yScrollPosition;

    const targetDiv = document.querySelector(
      `#section${brandChar}`
    ) as HTMLDivElement;
    const height = document.querySelector(
      ".shop_all_brands_wrapper .headroom-wrapper"
    ).scrollHeight;

    let currentYScrollPosition;
    if (targetDiv) {
      currentYScrollPosition = targetDiv.offsetTop - height + 92;
    }
    setYScrollPosition(currentYScrollPosition);

    if (
      previousYScrollPosition !== 0 &&
      previousYScrollPosition > currentYScrollPosition
    ) {
      currentYScrollPosition = currentYScrollPosition - 194;
    }
    window.scroll({
      top: currentYScrollPosition,
      left: 0,
      behavior: "smooth"
    });
    setSelectedBrandChar(brandChar);
  };

  const handleDropdownChange = useCallback(
    (tabData: { label: string; value: string }) => {
      changeCategory({ name: tabData.label, slug: tabData.value });
    },
    [tabs, changeCategory]
  );

  const dropdownData = useMemo(
    () => tabs.flatMap(tab => ({ label: tab.name, value: tab.slug })),
    [tabs]
  );

  return (
    <div className="brands-tabs-wrapper">
      <div className="container">
        <View.desktop>
          <div className="brand-category">
            <div className="brands-tabs">
              <ul>
                {map(tabs, tab => (
                  <li
                    key={tab.slug}
                    className={cn({
                      active: selectedTab.slug === tab.slug
                    })}
                    onClick={() => changeCategory(tab)}
                  >
                    <span className="tabs-text">{tab.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="brands-filter">
            <ul>
              {map(categoryData, brandChar => (
                <li
                  key={brandChar}
                  className={cn({
                    active: brandChar === selectedBrandChar
                  })}
                >
                  <p className="tabs-text">
                    <a
                      href={`#section${brandChar}`}
                      onClick={e => scrollHandler(e, brandChar)}
                    >
                      <span>{brandChar}</span>
                    </a>
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </View.desktop>
        <View.mobile className="mobile-brands-filter">
          <div className="dropdown-wrapper">
            <CustomDropdown
              data={dropdownData}
              selectedItem={{
                label: selectedTab.name,
                value: selectedTab.slug
              }}
              handleChange={handleDropdownChange}
              containerClass="brands_dropdown"
              scrollbarMaxHeight={150}
            />
          </div>
          <div className="brand-search">
            <input
              type="text"
              name="searchText"
              className="search-text-area"
              value={searchText || ""}
              onChange={onInputChange}
              placeholder={t("searchForBrands")}
              aria-label="Search"
            />
            <span className="search_icon" />
          </div>
        </View.mobile>
      </div>
    </div>
  );
};

export default CategoriesTabs;
